import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { th } from 'date-fns/locale';
import { lastValueFrom, Subject } from 'rxjs';
import { environment as env } from 'src/environments/environment';
import { GenerateSEBConfigModel, IExam, IExaminationSchedule, IQuestionCategory } from '../interface/dto';
import { ExamService } from './exam-service.service';
import { AuthService } from './http-services/authservice.service';

@Injectable({
  providedIn: 'root'
})
export class ExamScheduleService {
  private _selectAll: boolean = false;

  constructor(private httpClient: HttpClient, private auth: AuthService, private examService: ExamService) { }

  headers() {
    let headers = new HttpHeaders();
    headers = headers.set('Content-Type', 'application/json');
    headers = headers.set('Authorization', `Bearer ${this.auth.token}`);
    return headers;
  }

  reloadGrid = new Subject<any>();

  private _selectedIds: string[];
  private _selectedSchQuestionIds: string[];
  private _selectedSchedule: IExaminationSchedule[];

  public get selectAll(): boolean {
    return this._selectAll;
  }
  public set selectAll(value: boolean) {
    this._selectAll = value;
  }
  set selectedIds(ids: string[]) {
    //sessionStorage.setItem("selectedExamScheduleId", id)
    this._selectedIds = [...(ids || [])];
  }

  get selectedIds() {
    if (this._selectedSchedule == null || this._selectedSchedule.length === 0) {
      return [];
    }
    return this._selectedSchedule.map(x => x.id.toString());
  }

  set selectedSchQuestionId(ids: string[]) {
    this._selectedSchQuestionIds = [...(ids || [])];
  }

  get selectedSchQuestionId() {
    return this._selectedSchQuestionIds;
  }

  set selectedSchedules(schedules: IExaminationSchedule[]) {
    this._selectedSchedule = [...(schedules || [])];
    sessionStorage.setItem("selectedSchedules", JSON.stringify(schedules));
  }

  get selectedSchedules() {
    return JSON.parse(sessionStorage.getItem("selectedSchedules")) as IExaminationSchedule[]
  }

  async getExaminationSchedules(tableParam) {
    return await lastValueFrom(this.httpClient.patch(
      `${env.apiURL}examinationSchedule/`,
      tableParam, { headers: this.headers() }
    ), { defaultValue: [] });
  }

  async getExaminationSchedulesQuestions(tableParam) {
    return await lastValueFrom(this.httpClient.patch(
      `${env.apiURL}examinationSchedule/questions`,
      tableParam, { headers: this.headers() }
    ), { defaultValue: [] });
  }

  async getExaminationSchedulesEssayAnswers(tableParam) {
    return await lastValueFrom(this.httpClient.patch(
      `${env.apiURL}examinationSchedule/eassayanswers`,
      tableParam, { headers: this.headers() }
    ), { defaultValue: [] });
  }

  async getExaminationSchedulesBackup(tableParam) {
    return await lastValueFrom(this.httpClient.patch(
      `${env.apiURL}examinationSchedule/backups`,
      tableParam, { headers: this.headers() }
    ), { defaultValue: [] });
  }

  async getExaminationSchedulesAddedTimeLog(tableParam) {
    return await lastValueFrom(this.httpClient.patch(
      `${env.apiURL}examinationSchedule/addedtimelog`,
      tableParam, { headers: this.headers() }
    ), { defaultValue: [] });
  }

  async getExaminationSchedulesPausedTimeLog(tableParam) {
    return await lastValueFrom(this.httpClient.patch(
      `${env.apiURL}examinationSchedule/pausedtimelog`,
      tableParam, { headers: this.headers() }
    ), { defaultValue: [] });
  }

  async getExaminationSchedulesFlags(tableParam) {
    return await lastValueFrom(this.httpClient.patch(
      `${env.apiURL}examinationSchedule/flags`,
      tableParam, { headers: this.headers() }
    ), { defaultValue: [] });
  }


  async restartExaminationSchedules(data: any) {
    return await lastValueFrom(this.httpClient.post(
      `${env.apiURL}examinationSchedule/restart`,
      data, { headers: this.headers() }
    ), { defaultValue: [] });
  }

  async recomputeIssueScore() {
    return await lastValueFrom(this.httpClient.post(
      `${env.apiURL}examinationSchedule/recomputeissuescore`,
      this.selectedIds.map(x => parseInt(x)), { headers: this.headers() }
    ), { defaultValue: [] });
  }

  async addTime(data) {
    return await lastValueFrom(this.httpClient.post(
      `${env.apiURL}examinationSchedule/addtime`,
      data, { headers: this.headers() }
    ), { defaultValue: [] });
  }

  async pauseCandidate(data) {

    return await lastValueFrom(this.httpClient.post(
      `${env.apiURL}examinationSchedule/pauseunpauseexam`, data, { headers: this.headers() }
    ), { defaultValue: [] });
  }

  async changeStatus(data) {
    return await lastValueFrom(this.httpClient.post(
      `${env.apiURL}examinationSchedule/changestatus`,
      data, { headers: this.headers() }
    ), { defaultValue: [] });
  }

  async changeScheduleStatus(data) {

    return await lastValueFrom(this.httpClient.post(
      `${env.apiURL}examinationSchedule/changeexamschedulestartdatetime`,data, { headers: this.headers() }
    ), { defaultValue: [] });
  }
  async getExamScheduleScript() {
    let schId = this.selectedIds.map(x => parseInt(x))[0];
    return this.httpClient.get(`${env.apiURL}examinationSchedule/examschedulescript?schId=${schId}`, { headers: this.headers() }).toPromise();
  }

  async getExamScheduleExport(tableParam) {
    return await lastValueFrom(this.httpClient.patch(
      `${env.apiURL}examinationSchedule/export`,
      tableParam, { headers: this.headers() }
    ), { defaultValue: [] });
  }

  async getExaminationSchedulesFlagsExport(tableParam) {
    return await lastValueFrom(this.httpClient.patch(
      `${env.apiURL}examinationSchedule/flags/export`,
      tableParam, { headers: this.headers() }
    ), { defaultValue: [] });
  }

  async getSEBInstructionUrl() {
    return await lastValueFrom(this.httpClient.get(
      `${env.apiURL}examinationSchedule/${this.selectedSchedules[0].id}/sebinstructionpageurl`,
      { headers: this.headers() }
    ), { defaultValue: [] });
  }

  async generateSEBConfig(data: GenerateSEBConfigModel) {
    return lastValueFrom(this.httpClient.post(`${env.apiURL}examinationSchedule/generatesebconfig`, data, { headers: this.headers() }));
  }
  async getScheduleDetails(examinationScheduleId) {
    return this.httpClient.get(`${env.apiURL}examinationSchedule/scheduledetails?examinationScheduleId=${examinationScheduleId}`, { headers: this.headers() }).toPromise();
  }
  async saveScheduleDetails(data: IExaminationSchedule) {
    return this.httpClient.post(`${env.apiURL}examinationSchedule/savescheduledetails`, data, { headers: this.headers() }).toPromise();
  }
  async addSchedule(data: IExaminationSchedule) {
    return this.httpClient.post(`${env.apiURL}examinationSchedule/createexamschedule`, data, { headers: this.headers() }).toPromise();
  }
  async deleteSchedule(examinationScheduledata) {
    return this.httpClient.delete(`${env.apiURL}examinationSchedule/deleteexamschedule`, { headers: this.headers(), body: examinationScheduledata }).toPromise();
  }
  async flagSchedule(data: any) {
    return this.httpClient.post(`${env.apiURL}examinationSchedule/flagschedule`, data, { headers: this.headers() }).toPromise();
  }
}
